import { Key, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Box, Typography, useMediaQuery } from '@mui/material';

import {
  AssessmentButton,
  ChapterSelectedCard,
  ChipBadge,
  IStyles,
  Loader,
  NoContentCard,
  TopicCardPrime,
  deserify,
  getLocalStorage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { AssessmentEnum } from '@protos/learning_management/lms.db_pb';
import { interactionEvent } from '../../../Auth/auth.events';
import {
  ASSESMENT_LIBRARY,
  ASSESSMENT_CREATE,
  HOMEWORK,
  HOMEWORK_CHAPTER_SELECTION,
} from '../../../../routeHandling/RoutesNomenclature';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import {
  resetSelectedChapter,
  resetSelectedTasksInfo,
  resetSelectedTopic,
  setAssessmentData,
  setChapterWiseTopicInfo,
  setSelectedChapter,
} from '../../reducer/assessmentFlow.slice';
import ChapterResourcesDisplay from './components/ChapterResourcesDisplay';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from '../../../../reduxStore/reduxHooks';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';
import { getRoundedDownValue } from '../../../../utils/functions';
import { ModuleSchoolContentStatsData } from '@protos/learning_management/lms.common.apis_pb';

const styles: IStyles = {
  root: {
    boxSizing: 'border-box',
    backgroundColor: 'neutral.paleGrey',
    // display: 'flex',
    // flexDirection: { xs: 'column', md: 'row' },
    // gap: pxToRem(25),
    padding: {
      md: `${pxTovW(14)} ${pxToRem(20)}`,
      lg: `${pxTovW(14)} ${pxTovW(240)}`,
    },
    paddingTop: { md: pxTovW(40) },
  },
  textWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(14), md: pxTovW(10) },
    paddingX: { xs: pxToRem(20), md: 0 },
  },
  topicContainer: {
    flexBasis: { xs: '100%', md: '40%', lg: pxTovW(554) },
  },
  chapterBox: {
    padding: { xs: pxToRem(20), md: 0 },
    paddingBottom: { xs: pxToRem(20), md: pxTovW(46) },
  },
  resourceContainer: {
    width: '100%',
  },
  chooseTopicBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(7), md: pxTovW(18) },
  },
  topicLoader: { width: { xs: '100%', md: pxTovW(1200), lg: pxTovW(940) } },
  topicBox: {
    // width: { xs: '100%', md: pxTovW(1200), lg: pxTovW(940) },
    display: 'flex',
    flexDirection: 'column',
    background: 'white',
    '& > div': { borderBottom: '1px solid #cdc1ff' },
  },
};
export default function HwTopicSelection() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { subject_id, chapter_id } = useParams();
  const location = useLocation();
  const pathname = location.pathname;
  const { isOffline } = useDownloadContext();
  const pathSegments = location.pathname.split('/');
  const isTopicsSelection = pathname.includes('/hw-topics-selection');
  const teacher_id = getLocalStorage('userId');
  // Getting current class and subject selected
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const { chapter_resources, subjectwise_chapters_info, chapterwise_topic } =
    deserify(useAppSelector((state) => state.assessment));

  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  // const [schoolContentStats, setSchoolContentStats] = useState<
  //   ModuleSchoolContentStatsData | undefined
  // >();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    ContentCommonAPIServiceV2ClientWithStatusCodeHandler,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsCommonAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const topicNavigation = async (topicId?: string) => {
    navigate(`${HOMEWORK}${ASSESSMENT_CREATE}/${topicId}/${chapter_id}`);
    await interactionEvent({
      context: 'Choose_topic',
      name: 'TOPIC_OPEN',
      pathSegments: pathSegments,
      isOffline: isOffline,
    });
  };
  const { setSelectedFunction, isIpadOnly } = useGlobalContext();

  useEffect(() => {
    const onBackClick = () => {
      navigate(`${HOMEWORK_CHAPTER_SELECTION}/${subject_id}`);
    };
    setSelectedFunction(() => onBackClick);
    dispatch(resetSelectedChapter());
    dispatch(resetSelectedTopic());
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  const findSelectedChapterData = () => {
    const selectedChapter = subjectwise_chapters_info?.chapters.find(
      (chapter: { chapterId: number }) =>
        chapter_id && +chapter_id === chapter.chapterId
    );
    if (selectedChapter) {
      return {
        name: selectedChapter.chapterTitle,
        chapterNo: selectedChapter.chapterNo,
        image: getMediaBasePath(
          selectedChapter.posterImagesUrl,
          'processedMediaBucket'
        ),
      };
    }
    return { name: '', image: '' };
  };

  async function fetchChapterTopicInfo({
    subjectId,
    chapterId,
  }: {
    subjectId: string;
    chapterId: string;
  }) {
    try {
      setLoading('loading');

      const response =
        await ContentCommonAPIServiceV2ClientWithStatusCodeHandler.fetchChapterTopicInfoV2(
          {
            personId: Number(teacher_id),
            // personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            personType: 'TEACHER',
            subjectId: Number(subjectId),
            chapterId: Number(chapterId),
            sectionId: class_subject_info?.sectionId,
            bookId: class_subject_info?.bookId,
            isAssessFlow: true,
          }
        );

      if (response?.data) {
        const data = response.data;
        // console.log(typeof data, data);
        data.topics.sort((a, b) => a.topicNo - b.topicNo);
        dispatch(setChapterWiseTopicInfo(data));
      } else {
        // setError(new Error('Login failed'));
      }

      setLoading('completed');
    } catch (err) {
      setLoading('error');
      // setError(err);
      console.log(err);
    }
  }

  const fetchAssessmentsByModule = async (
    subjectId: number,
    chapterId: number | undefined
  ) => {
    try {
      setLoading('loading');
      const response =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentsByModule(
          {
            personId: teacher_id,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            subjectId: subjectId,
            assessmentType: AssessmentEnum.ASSESSMENT_HOMEWORK,
            chapterId: chapter_id ? Number(chapterId) : undefined,
          }
        );
      if (response?.data) {
        dispatch(setAssessmentData(response.data));
      }
      setLoading('completed');
    } catch (err) {
      setLoading('error');
      console.log(err);
    }
  };

  // -----------------------------------------------------------

  useEffect(() => {
    // if (chapter_id) fetchChapterTopicInfo();
    if (subject_id && chapter_id) {
      fetchChapterTopicInfo({ subjectId: subject_id, chapterId: chapter_id });
      // fetchAssessmentsByModule(Number(subject_id), Number(chapter_id));
      dispatch(resetSelectedTasksInfo());
      dispatch(resetSelectedTopic());
    }
  }, [chapter_id]);
  // console.log('subject_topic_info', chapterwise_topic);

  // -----------------------------------------------------------

  return (
    <Box sx={styles.root}>
      <Box sx={styles.topicContainer}>
        <Box sx={styles.chapterBox}>
          <ChapterSelectedCard
            image={findSelectedChapterData().image}
            chapterName={
              findSelectedChapterData().name
                ? findSelectedChapterData().name
                : ''
            }
            className={
              class_subject_info
                ? class_subject_info?.classname + class_subject_info?.section
                : ''
            }
            subject={class_subject_info ? class_subject_info?.subject : ''}
            headingData={{
              ...(chapterwise_topic?.chapterStats?.questionCount && {
                yellowData: {
                  value: getRoundedDownValue(
                    chapterwise_topic.chapterStats.questionCount
                  ),
                  text: 'Questions',
                },
              }),
              ...(chapterwise_topic?.chapterStats?.learningOutcomeCount && {
                blueData: {
                  value: getRoundedDownValue(
                    chapterwise_topic.chapterStats.learningOutcomeCount
                  ),
                  text: 'Learning Outcomes',
                },
              }),
            }}
            headingRightElement={
              isMobile ? null : (
                <Box>
                  <AssessmentButton
                    backgroundColor="#CBEECD"
                    imageName="library-group"
                    variant="regular"
                    clickHandler={async () => {
                      dispatch(
                        setSelectedChapter({
                          chapterId: chapter_id,
                          chapterTitle: findSelectedChapterData().name,
                          chapterNo: findSelectedChapterData().chapterNo,
                        })
                      );
                      navigate(`${ASSESMENT_LIBRARY}`);
                      await interactionEvent({
                        context: 'Navigation_bar',
                        name: 'CHOOSE_FROM_LIBRARY',
                        pathSegments: pathSegments,
                        isOffline: isOffline,
                      });
                    }}
                    heading={{ bold: 'Choose', regular: 'From Library' }}
                    headingData={[
                      ...(chapterwise_topic?.chapterStats?.assessmentCount
                        ? [
                            {
                              value: getRoundedDownValue(
                                chapterwise_topic?.chapterStats?.assessmentCount
                              ),
                              text: 'Ready Assessments',
                            },
                          ]
                        : []),
                    ]}
                  />
                </Box>
              )
            }
          />

          {isMobile && (
            <Box sx={{ mt: { xs: pxToRem(20), md: pxTovW(0) } }}>
              <AssessmentButton
                backgroundColor="#CBEECD"
                imageName="library-group"
                variant="small"
                clickHandler={() => {
                  dispatch(
                    setSelectedChapter({
                      chapterId: chapter_id,
                      chapterTitle: findSelectedChapterData().name,
                      chapterNo: findSelectedChapterData().chapterNo,
                    })
                  );
                  navigate(`${ASSESMENT_LIBRARY}`);
                }}
                heading={{ bold: 'Choose', regular: 'From Library' }}
                headingData={[
                  ...(chapterwise_topic?.chapterStats?.assessmentCount
                    ? [
                        {
                          value: getRoundedDownValue(
                            chapterwise_topic?.chapterStats?.assessmentCount
                          ),
                          text: 'Ready Assessments',
                        },
                      ]
                    : []),
                ]}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: pxToRem(20), md: pxTovW(35) },
        }}
      >
        <Box
          sx={{
            ...styles.chooseTopicBox,
            flexBasis: {
              xs: '100%',
              md:
                chapter_resources?.categoryResourceMap.length === 0
                  ? '100%'
                  : '40%',
              lg:
                chapter_resources?.categoryResourceMap.length === 0
                  ? '100%'
                  : pxTovW(554),
            },
          }}
        >
          <Box sx={styles.textWithBadge}>
            <Typography variant="h2">Choose a Topic</Typography>
            <Typography variant="h3">
              <ChipBadge
                label={chapterwise_topic?.topics.length}
                color="primary"
                size="small"
              />
            </Typography>
          </Box>

          {loading === 'loading' ? (
            <Box sx={styles.topicLoader}>
              <Loader />
            </Box>
          ) : loading === 'error' ? (
            <Box sx={styles.topicLoader}>
              <NoContentCard
                variant="error"
                icon="error"
                text="Error Occurred"
              />
            </Box>
          ) : chapterwise_topic && chapterwise_topic.topics.length > 0 ? (
            <Box
              sx={{
                ...styles.topicBox,
                width: {
                  xs: '100%',
                  md:
                    chapter_resources?.categoryResourceMap.length === 0
                      ? '100%'
                      : pxTovW(1200),
                  lg:
                    chapter_resources?.categoryResourceMap.length === 0
                      ? '100%'
                      : pxTovW(940),
                },
              }}
            >
              {chapterwise_topic?.topics.map(
                (topic, index: Key | null | undefined) => (
                  <TopicCardPrime
                    key={index}
                    title={topic.topicTitle}
                    homeWorkCount={topic.topicStats?.assessmentCount}
                    questionsCount={topic.topicStats?.questionCount}
                    viewAllClickHandler={() =>
                      topicNavigation(topic.topicId.toString())
                    }
                    rootStyle={{
                      paddingX: { xs: pxToRem(20), md: pxTovW(32) },
                      paddingY: { xs: pxToRem(11), md: pxTovW(36) },
                    }}
                  />
                )
              )}
            </Box>
          ) : (
            <Box sx={styles.topicLoader}>
              <NoContentCard
                variant="white"
                icon="coming-soon-yellow"
                text="No topics to show"
              />
            </Box>
          )}
        </Box>

        {/* right panel */}
        {chapter_resources?.categoryResourceMap.length !== 0 && (
          <Box sx={styles.resourceContainer}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  ...styles.textWithBadge,
                  paddingY: { xs: pxToRem(20), md: 0 },
                }}
              >
                <Typography
                  variant={isIpadOnly ? 'h3' : 'h2'}
                  sx={{
                    wordBreak: 'break-word',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  Chapter Resources
                </Typography>
                <Typography variant="h3">
                  <ChipBadge
                    label={
                      chapter_resources?.categoryResourceMap
                        ?.map(
                          (obj: { categoryResources: string | any[] }) =>
                            obj.categoryResources.length
                        )
                        .reduce((a: any, b: any) => a + b, 0) || 0
                    }
                    color="primary"
                    size="small"
                  />
                </Typography>
              </Box>

              {/* <Box
            sx={{
              display: { xs: 'none', md: 'unset' },
            }}
          >
            <AssessmentButton
              text="Choose"
              subText={`From Library (${roundingAssessmentNumber(
                Number(100)
              )} ready to use assessments)`}
              backgroundColor="#CBEECD"
              imageName="library-group"
              variant="small"
              clickHandler={() => {
                dispatch(
                  setSelectedChapter({
                    chapterId: chapter_id,
                    chapterTitle: findSelectedChapterData().name,
                  })
                );
                navigate(`${ASSESMENT_LIBRARY}`);
              }}
            />
          </Box> */}
            </Box>

            <Box sx={{ width: '100%' }}>
              <ChapterResourcesDisplay />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
